import type { RuntimeAdapterConfig } from "./config.ts";

export const TIME_RANGE_DAYS: Record<string, number> = {
  "last-7-days": 7,
  "last-30-days": 30,
  "last-90-days": 90,
  "last-year": 365,
  "last-2-years": 730,
  "last-5-years": 1825,
};

/**
 * Rolling window for recurring jobs: each run covers sales since the job's last
 * completed run, minus an overlap so a sale published late is still caught.
 * The orchestrator turns it into an absolute window per run
 * (resolveRollingWindow), so every worker sees ordinary dateFrom/dateTo.
 */
export const ROLLING_TIME_RANGE = "since-last-success";
export const ROLLING_OVERLAP_DAYS = 7;
/** Window for a rolling job's first run, when no run has completed yet. */
export const ROLLING_FIRST_RUN_DAYS = 30;

/**
 * Fields that make up a date window. A job has exactly one window, in
 * scrape_jobs.config (George, 2026-10-13): job_adapters.config never sets one, and
 * job-orchestrator drops these keys from it when merging.
 */
export const DATE_WINDOW_KEYS = [
  "dateFilterMode",
  "timeRange",
  "dateFrom",
  "dateTo",
  "rollingFirstRunDays",
] as const;

export function withoutDateWindow<T extends Record<string, unknown>>(config: T): T {
  const rest: Record<string, unknown> = { ...config };
  for (const key of DATE_WINDOW_KEYS) delete rest[key];
  return rest as T;
}

export interface ResolvedDateWindow {
  dateFilterMode: "relative" | "absolute";
  effectiveDateFrom?: string;
  effectiveDateTo?: string;
  effectiveMaxAgeDays?: number;
}

export function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function parseIsoDate(value?: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

export function isRollingWindow(config: RuntimeAdapterConfig): boolean {
  return (
    config.dateFilterMode !== "absolute" &&
    config.timeRange === ROLLING_TIME_RANGE
  );
}

/**
 * Resolves timeRange "since-last-success" into an absolute window ending on
 * referenceDate. lastSuccessAt is when the job's last completed run started;
 * with none, the window is the config's rollingFirstRunDays (default 30).
 * Any other config is returned unchanged.
 */
export function resolveRollingWindow(
  config: RuntimeAdapterConfig,
  lastSuccessAt: string | null,
  referenceDate: Date,
): RuntimeAdapterConfig {
  if (!isRollingWindow(config)) return config;
  const lastSuccess = lastSuccessAt ? new Date(lastSuccessAt) : null;
  const from = new Date(referenceDate);
  if (lastSuccess && !Number.isNaN(lastSuccess.getTime())) {
    from.setTime(lastSuccess.getTime());
    from.setUTCDate(from.getUTCDate() - ROLLING_OVERLAP_DAYS);
  } else {
    from.setUTCDate(
      from.getUTCDate() - (config.rollingFirstRunDays ?? ROLLING_FIRST_RUN_DAYS),
    );
  }
  return {
    ...config,
    dateFilterMode: "absolute",
    timeRange: undefined,
    dateFrom: toIsoDate(from),
    dateTo: toIsoDate(referenceDate),
  };
}

export function resolveEffectiveDateWindow(
  config: RuntimeAdapterConfig,
  referenceDate: Date,
): ResolvedDateWindow {
  const dateFilterMode =
    config.dateFilterMode === "absolute" ? "absolute" : "relative";

  if (dateFilterMode === "absolute") {
    const absoluteFrom = parseIsoDate(config.dateFrom);
    const absoluteTo = parseIsoDate(config.dateTo);
    const effectiveDateFrom = absoluteFrom
      ? toIsoDate(absoluteFrom)
      : undefined;
    const effectiveDateTo = absoluteTo ? toIsoDate(absoluteTo) : undefined;
    const effectiveMaxAgeDays =
      absoluteFrom && absoluteTo
        ? Math.max(
            1,
            Math.round(
              (absoluteTo.getTime() - absoluteFrom.getTime()) / 86400000,
            ),
          )
        : undefined;

    return {
      dateFilterMode: "absolute",
      effectiveDateFrom,
      effectiveDateTo,
      effectiveMaxAgeDays,
    };
  }

  const days = config.timeRange
    ? TIME_RANGE_DAYS[config.timeRange]
    : undefined;
  if (!days) {
    return { dateFilterMode: "relative" };
  }

  const end = new Date(referenceDate);
  const start = new Date(referenceDate);
  start.setDate(end.getDate() - days);
  return {
    dateFilterMode: "relative",
    effectiveDateFrom: toIsoDate(start),
    effectiveDateTo: toIsoDate(end),
    effectiveMaxAgeDays: days,
  };
}

/**
 * Check if a sale date falls outside a relative time range.
 * Returns true if the date is too old (with 30-day buffer).
 */
export function isSaleDateOutOfRange(
  saleDate: string | null,
  timeRange: string | null | undefined,
): boolean {
  if (!saleDate || !timeRange) return false;
  const maxDays = TIME_RANGE_DAYS[timeRange];
  if (!maxDays) return false;

  const saleDateMs = new Date(saleDate).getTime();
  if (Number.isNaN(saleDateMs)) return false;

  const cutoff = Date.now() - maxDays * 24 * 60 * 60 * 1000;
  const cutoffWithBuffer = cutoff - 30 * 24 * 60 * 60 * 1000;
  return saleDateMs < cutoffWithBuffer;
}

/**
 * Check if a sale date falls outside an absolute date range.
 */
export function isSaleDateOutsideCustomRange(
  saleDate: string | null,
  dateFrom: string | null | undefined,
  dateTo: string | null | undefined,
): boolean {
  if (!saleDate) return false;
  const saleTs = new Date(saleDate).getTime();
  if (Number.isNaN(saleTs)) return false;

  if (dateFrom) {
    const fromTs = new Date(dateFrom).getTime();
    if (!Number.isNaN(fromTs) && saleTs < fromTs) return true;
  }
  if (dateTo) {
    const toTs = new Date(dateTo).getTime();
    if (!Number.isNaN(toTs) && saleTs > toTs) return true;
  }
  return false;
}
